<template>
  <div :class="['player-wrapper', { dvr, live: !dvr }]">
    <SmartPlayer
      class="camera-player"
      :live-token="archiveToken"
      :archive-token="dvr ? archiveToken : null"
      :available-archive-fragments="dvr ? availableArchiveFragments : null"
      :camera-number="cameraNumber"
      :domain="domain"
      :port="port"
      :initial-time-shift="dvr ? initialTimeShiftValue : null"
      :http-protocol="protocols.http"
      vendor-name="UMS"
      :ws-protocol="protocols.ws"
      :stream-count="1"
      :initial-stream-number="1"
      :use-simple-interface="!dvr"
      :get-line-fragments="dvr ? getLineFragments : null"
      @download-video="downloadVideo"
      :initial-low-latency-mode="false"
      :is-mobile="isMobile"
    />
  </div>
</template>

<script>
import SmartPlayer from 'camsng-frontend-shared/components/smartPlayer/SmartPlayer.vue';

export default {
  name: 'Player',
  components: { SmartPlayer },
  data() {
    const initialTimeShift = new Date();
    initialTimeShift.setMinutes(initialTimeShift.getMinutes() - 10, 0, 0);

    return {
      archiveToken: null,
      cameraNumber: null,
      domain: '',
      port: null,
      initialTimeShift,
      availableArchiveFragments: [],
      timeoutIdForReceivingRecordingStatus: null,
      dvr: false,
      from: 0,
      isLoading: false,
      lastTouchEnd: 0,
    };
  },
  created() {
    this.initializePlayer();
    this.updateViewportHeight();
    window.addEventListener('resize', this.updateViewportHeight);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateViewportHeight);
    this.cleanupEventListeners();
    clearTimeout(this.timeoutIdForReceivingRecordingStatus);
  },
  computed: {
    protocols() {
      return {
        http: window.location.protocol === 'https:' ? 'https' : 'http',
        ws: window.location.protocol === 'https:' ? 'wss' : 'ws',
      };
    },
    isMobile() {
      return window.innerWidth <= 768;
    },
    initialTimeShiftValue() {
      return this.from ? new Date(this.from * 1000) : this.initialTimeShift;
    },
  },
  methods: {
    initializePlayer() {
      this.parseUrlParams();
      this.setupEventListeners();
      if (this.dvr) this.fetchArchiveFragments();
    },
    updateViewportHeight() {
      const height = `${window.innerHeight}px`;
      document.documentElement.style.setProperty('--viewport-height', height);
    },
    parseUrlParams() {
      const urlParams = new URLSearchParams(window.location.search);
      this.archiveToken = urlParams.get('token') || null;
      this.dvr = urlParams.get('dvr');
      const pathSegments = window.location.pathname.split('/');
      this.cameraNumber = pathSegments[1] || null;
      this.domain = window.location.hostname;
      this.from = urlParams.get('from') || 0;
      this.port = window.location.port;
    },
    // Запрос доступных фрагментов архива
    async fetchArchiveFragments() {
      if (!this.dvr) return;
      this.isLoading = true;
      try {
        const currentTimestamp = Math.floor(Date.now() / 1000);
        const url = `${this.protocols.http}://${this.domain}:${this.port}/${this.cameraNumber}/recording_status.json?request=ranges&from=${this.from}&to=${currentTimestamp}`;
        const response = await fetch(this.archiveToken ? `${url}&token=${this.archiveToken}` : url);
        const data = await response.json();
        this.availableArchiveFragments = data[0].ranges.map((range) => {
          const from = new Date(range.from * 1000);
          const to = new Date(from.getTime() + range.duration * 1000);
          return [from, to];
        });
      } catch (error) {
        console.error('Ошибка при получении архивных фрагментов:', error);
      } finally {
        this.timeoutIdForReceivingRecordingStatus = setTimeout(this.fetchArchiveFragments, 7000);
        this.isLoading = false;
      }
    },
    // Отрисовка архива на таймлайне
    async getLineFragments(leftBoundary, rightBoundary) {
      return this.availableArchiveFragments
        .filter(([dateStart, dateEnd]) => +leftBoundary < +dateEnd && +rightBoundary > +dateStart)
        .map(([dateStart, dateEnd]) => [dateStart, dateEnd, '#bbbbbb', 0]);
    },
    downloadVideo([getUrl]) {
      const downloadUrl = getUrl(this.archiveToken);
      window.open(downloadUrl, '_blank');
    },
    // блокировка ненужных событий для мобильных браузеров
    setupEventListeners() {
      document.addEventListener('touchend', this.handleTouchEnd, { passive: false });
      document.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    },
    cleanupEventListeners() {
      document.removeEventListener('touchend', this.handleTouchEnd);
      document.removeEventListener('touchmove', this.handleTouchMove);
    },
    handleTouchEnd(event) {
      const now = new Date().getTime();
      if (now - this.lastTouchEnd <= 500) {
        event.preventDefault();
      }
      this.lastTouchEnd = now;
    },
    handleTouchMove(event) {
      if (event.touches.length > 1 || window.scrollY === 0) {
        event.preventDefault();
      }
    },
  },
};
</script>

<style scoped>
.player-wrapper {
  width: 100%;
  margin: 0 auto;
  position: relative;
  height: var(--viewport-height);
}
.camera-player {
  width: 100%;
  height: 100%;
}
.player-wrapper.dvr .camera-player {
  height: calc(var(--viewport-height) - 130px);
}
@media (max-width: 768px) and (orientation: portrait) {
  .player-wrapper.dvr .camera-player {
    height: calc(var(--viewport-height) - 250px);
  }
  ::v-deep(.smart-player-controls__main) {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  ::v-deep(.smart-player-controls__group_start) {
    display: flex;
    margin-left: 16px;
    gap: 20px;
  }
  ::v-deep(.smart-player-controls__group_end) {
    justify-content: space-around;
  }
}
@media (max-width: 768px) and (orientation: landscape) {
  .player-wrapper.dvr .camera-player {
    height: calc(var(--viewport-height) - 100px);
  }
  .player-wrapper.dvr ::v-deep(.smart-player-controls__main) {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: -20px;
  }
  ::v-deep(.smart-player-controls__group_end) {
    justify-content: space-around;
  }
}
</style>
